import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, Grid, Tabs, Tab, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, TextField, InputAdornment,
    CircularProgress, Alert, Container, Stack, Button, Tooltip as MuiTooltip,
    useTheme
} from '@mui/material';
import { FaSearch, FaCheckCircle, FaExclamationCircle, FaExchangeAlt, FaList, FaFileInvoiceDollar, FaFileExport, FaSyncAlt } from 'react-icons/fa';
import { useCompetence } from '../contexts/CompetenceContext';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getResultadoCompleto = async (mes = null) => {
    // If mes is provided, use dashboard/data?mes=XXX, otherwise use ultimo_resultado
    const endpoint = mes ? `/dashboard/data?mes=${mes}` : '/dashboard/ultimo_resultado';
    console.log(`[getResultadoCompleto] mes=${mes}, endpoint=${endpoint}`);
    try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        console.log(`[getResultadoCompleto] Calling: ${API_URL}${endpoint}`);
        const response = await axios.get(API_URL + endpoint, { headers });
        console.log(`[getResultadoCompleto] Response status: ${response.status}`, response.data);
        return response.data;
    } catch (error) {
        console.error(`[getResultadoCompleto] Error:`, error);
        if (error.response) return error.response.data;
        return { status: "Erro", mensagem: "Erro de conexão ao tentar buscar o resultado." };
    }
};

const formatCurrency = (value) => {
    if (typeof value !== 'number') value = 0;
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const RelatorioConferencia = () => {
    const theme = useTheme();
    const { globalCompetence } = useCompetence();

    // Estado para a tabela de conferência consolidada
    const [dataConferencia, setDataConferencia] = useState([]);
    // Estado para a tabela de recebidos detalhada
    const [dataRecebidos, setDataRecebidos] = useState([]);
    // Estado para a nova tabela de Rastreabilidade
    const [dataRastreabilidade, setDataRastreabilidade] = useState([]);
    const [filterOrigem, setFilterOrigem] = useState('');
    const [filterDestino, setFilterDestino] = useState('');
    // Estado para a tabela de Contas Contábeis
    const [dataContabil, setDataContabil] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mesReferencia, setMesReferencia] = useState('');

    // Estado para controlar a aba ativa
    const [activeTab, setActiveTab] = useState(0); // 0: conferencia, 1: contabil, 2: detalhe, 3: rastreabilidade

    // Estado para reprocessamento
    const [reprocessing, setReprocessing] = useState(false);
    const [reprocessMessage, setReprocessMessage] = useState(null);

    // Função para reprocessar o mês atual
    const handleReprocessMonth = async () => {
        if (!globalCompetence) {
            alert('Selecione uma competência primeiro.');
            return;
        }

        setReprocessing(true);
        setReprocessMessage(null);

        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const response = await axios.post(
                `${API_URL}/reprocessar_mes`,
                { mes: globalCompetence },
                { headers }
            );

            if (response.data.status === 'Processing') {
                // Aguardar um pouco e recarregar os dados
                setReprocessMessage('Reprocessando... Aguarde.');

                // Poll for completion or just wait a few seconds
                setTimeout(async () => {
                    setReprocessMessage('Recarregando dados...');
                    window.location.reload();
                }, 5000);
            } else {
                setReprocessMessage(response.data.mensagem || 'Erro ao reprocessar.');
                setReprocessing(false);
            }
        } catch (err) {
            setReprocessMessage(err.response?.data?.mensagem || 'Erro ao reprocessar o mês.');
            setReprocessing(false);
        }
    };

    // Fetch data when global competence changes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Only fetch the main resultado completo, balancete endpoint is not needed
                const resRateio = await getResultadoCompleto(globalCompetence);

                console.log('[fetchData] Response received:', resRateio);
                console.log('[fetchData] Response type:', typeof resRateio);
                console.log('[fetchData] Response status:', resRateio?.status);
                console.log('[fetchData] Response mes:', resRateio?.mes);

                let hasError = false;

                // --- Processamento 1 (Dados de Rateio) ---
                if (resRateio && (resRateio.status === "Concluído" || resRateio.status === "Sucesso")) {
                    console.log('[fetchData] ✓ Status check passed, processing data...');
                    setMesReferencia(resRateio.mes || 'Último Mês');

                    const relatorio = resRateio.relatorio_analitico || {};
                    const recebidos = resRateio.detalhamento_alocacoes || {};
                    const enviados = resRateio.origens_detalhadas || {};
                    const listaConferencia = [];
                    const listaRecebidos = [];
                    const listaRastreabilidade = [];

                    for (const centro in relatorio) {
                        const info = relatorio[centro];

                        const valorDespesas = (info['Custo Direto'] || 0) + (info['Custo Indireto'] || 0);
                        const valorReceitas = (info['Receita Inicial'] || 0);

                        const receitasRecebidas = (recebidos[centro] || []).reduce((acc, transacao) => {
                            return acc + (transacao.tipo === 'Receita' ? (transacao.valor || 0) : 0);
                        }, 0);
                        const despesasRecebidas = (recebidos[centro] || []).reduce((acc, transacao) => {
                            const tipo = transacao.tipo || 'Despesa';
                            return acc + (tipo === 'Despesa' ? (transacao.valor || 0) : 0);
                        }, 0);

                        const receitasEnviadas = (enviados[centro] || []).reduce((acc, transacao) => {
                            return acc + (transacao.tipo === 'Receita' ? (transacao.valor || 0) : 0);
                        }, 0);
                        const despesasEnviadas = (enviados[centro] || []).reduce((acc, transacao) => {
                            const tipo = transacao.tipo || 'Despesa';
                            return acc + (tipo === 'Despesa' ? (transacao.valor || 0) : 0);
                        }, 0);

                        const saldoReceitas = valorReceitas + receitasRecebidas - receitasEnviadas;
                        const saldoDespesas = valorDespesas + despesasRecebidas - despesasEnviadas;

                        listaConferencia.push({
                            centro: centro,
                            valorReceitas: valorReceitas,
                            valorDespesas: valorDespesas,
                            receitasRecebidas: receitasRecebidas,
                            receitasEnviadas: receitasEnviadas,
                            despesasRecebidas: despesasRecebidas,
                            despesasEnviadas: despesasEnviadas,
                            saldoReceitas: saldoReceitas,
                            saldoDespesas: saldoDespesas
                        });
                    }

                    for (const destino in recebidos) {
                        recebidos[destino].forEach(transacao => {
                            listaRecebidos.push({
                                destino: destino,
                                origem: transacao.origem,
                                valor: transacao.valor,
                                regra: `${transacao.regra} (${transacao.tipo || 'Despesa'})`
                            });

                            listaRastreabilidade.push({
                                id: `${transacao.origem}-${destino}-${transacao.valor}-${Math.random()}`,
                                origem: transacao.origem,
                                destino: destino,
                                valor: transacao.valor,
                                tipo: transacao.tipo || 'Despesa',
                                regra: transacao.regra
                            });
                        });
                    }

                    console.log('[fetchData] Data processed:', {
                        conferencia: listaConferencia.length,
                        recebidos: listaRecebidos.length,
                        rastreabilidade: listaRastreabilidade.length
                    });

                    setDataConferencia(listaConferencia);
                    setDataRecebidos(listaRecebidos);
                    setDataRastreabilidade(listaRastreabilidade);

                    // Populate accounting entries from balancete_por_conta
                    if (resRateio.balancete_por_conta && Array.isArray(resRateio.balancete_por_conta)) {
                        setDataContabil(resRateio.balancete_por_conta);
                        console.log('[fetchData] Balancete entries:', resRateio.balancete_por_conta.length);
                    } else {
                        setDataContabil([]);
                        console.log('[fetchData] No balancete_por_conta in response');
                    }

                } else {
                    console.error('[fetchData] ✗ Status check FAILED');
                    console.error('[fetchData] resRateio exists?', !!resRateio);
                    console.error('[fetchData] status value:', resRateio?.status);
                    console.error('[fetchData] status === "Concluído"?', resRateio?.status === "Concluído");
                    console.error('[fetchData] status === "Sucesso"?', resRateio?.status === "Sucesso");
                    setError(resRateio.mensagem || "Não foi possível carregar os dados de rateio.");
                    hasError = true;
                }

                // --- Processamento 2 (Dados do Balancete por Conta) ---
                // COMMENTED OUT: getUltimoBalancete endpoint returns 404
                // This data is not critical for the main conference report
                /*
                if (resBalancete && resBalancete.status === "Sucesso" && resBalancete.data) {
                    const agrupado = resBalancete.data.reduce((acc, item) => {
                        const conta = item.Conta || 'Não Identificada';
                        const valor = parseFloat(item.Valor) || 0;
                        if (!acc[conta]) acc[conta] = 0;
                        acc[conta] += valor;
                        return acc;
                    }, {});

                    const listaContabil = Object.entries(agrupado).map(([conta, valor]) => ({
                        conta,
                        valor
                    }));

                    setDataContabil(listaContabil);
                } else {
                    if (!hasError) {
                        setError(resBalancete.mensagem || "Não foi possível carregar os dados do balancete.");
                    }
                }
                */

            } catch (err) {
                setError(err.message || "Erro desconhecido. Verifique o console.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [globalCompetence]);

    const filteredRastreabilidade = dataRastreabilidade.filter(item => {
        const matchOrigem = filterOrigem ? item.origem.toLowerCase().includes(filterOrigem.toLowerCase()) : true;
        const matchDestino = filterDestino ? item.destino.toLowerCase().includes(filterDestino.toLowerCase()) : true;
        return matchOrigem && matchDestino;
    });

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>Carregando relatórios de conferência...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ m: 4 }}>Erro: {error}</Alert>;
    }

    // Export handlers
    const handleExportCSV = () => {
        let exportData = [];
        let filename = '';
        let columns = [];

        if (activeTab === 0) {
            exportData = dataConferencia;
            filename = `conferencia_saldos_${mesReferencia}`;
            columns = [
                { key: 'centro', label: 'Centro de Custo' },
                { key: 'valorReceitas', label: 'Receitas (Balancete)' },
                { key: 'valorDespesas', label: 'Despesas (Balancete)' },
                { key: 'saldoReceitas', label: 'Saldo Receitas' },
                { key: 'saldoDespesas', label: 'Saldo Despesas' }
            ];
        } else if (activeTab === 1) {
            exportData = dataContabil;
            filename = `lancamentos_contabeis_${mesReferencia}`;
            columns = [
                { key: 'conta', label: 'Conta Contábil' },
                { key: 'valor', label: 'Valor Total' }
            ];
        } else if (activeTab === 2 || activeTab === 3) {
            exportData = activeTab === 2 ? dataRecebidos : filteredRastreabilidade;
            filename = activeTab === 2 ? `rateios_${mesReferencia}` : `rastreabilidade_${mesReferencia}`;
            columns = [
                { key: 'origem', label: 'Origem' },
                { key: 'destino', label: 'Destino' },
                { key: 'valor', label: 'Valor' },
                { key: 'regra', label: 'Regra' }
            ];
        }

        if (exportData.length === 0) return;

        const headers = columns.map(c => c.label).join(';');
        const rows = exportData.map(row =>
            columns.map(c => {
                const val = row[c.key];
                return typeof val === 'number'
                    ? val.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                    : val || '';
            }).join(';')
        );
        const csv = '\uFEFF' + [headers, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${filename}.csv`;
        link.click();
    };

    const handlePrint = () => window.print();

    return (
        <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FaCheckCircle color="#4f46e5" /> Relatório de Conferência de Saldos
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Visualização detalhada do fluxo de custos para o mês de <strong>{mesReferencia}</strong>.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <MuiTooltip title="Exportar para CSV">
                        <Button variant="outlined" startIcon={<FaFileExport />} onClick={handleExportCSV} sx={{ textTransform: 'none' }}>
                            CSV
                        </Button>
                    </MuiTooltip>
                    <MuiTooltip title="Imprimir">
                        <Button variant="outlined" onClick={handlePrint} sx={{ textTransform: 'none' }}>
                            Imprimir
                        </Button>
                    </MuiTooltip>
                    <MuiTooltip title="Reprocessar este mês com as regras de alocação atuais">
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={reprocessing ? <CircularProgress size={16} color="inherit" /> : <FaSyncAlt />}
                            onClick={handleReprocessMonth}
                            disabled={reprocessing}
                            sx={{ textTransform: 'none' }}
                        >
                            {reprocessing ? 'Reprocessando...' : 'Reprocessar Mês'}
                        </Button>
                    </MuiTooltip>
                </Stack>
            </Stack>

            <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="1. Conferência de Saldos" icon={<FaList />} iconPosition="start" />
                <Tab label="2. Lançamentos Contábeis" icon={<FaFileInvoiceDollar />} iconPosition="start" />
                <Tab label="3. Detalhe dos Rateios" icon={<FaExchangeAlt />} iconPosition="start" />
                <Tab label="4. Rastreabilidade" icon={<FaSearch />} iconPosition="start" />
            </Tabs>

            {/* Tabela 1: Conferência de Saldos (Consolidada) */}
            {activeTab === 0 && (
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="h6">Conferência de Saldos por Centro de Custo</Typography>
                    </Box>
                    <TableContainer sx={{ maxHeight: '60vh' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Centro de Custo</TableCell>
                                    <TableCell align="right">Receitas (Balancete)</TableCell>
                                    <TableCell align="right">Despesas (Balancete)</TableCell>
                                    <TableCell align="right">Receitas Recebidas</TableCell>
                                    <TableCell align="right">Receitas Enviadas</TableCell>
                                    <TableCell align="right">Despesas Recebidas</TableCell>
                                    <TableCell align="right">Despesas Enviadas</TableCell>
                                    <TableCell align="right">Saldo Final Receitas</TableCell>
                                    <TableCell align="right">Saldo Final Despesas</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dataConferencia.map((row, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell sx={{ fontWeight: 'medium' }}>{row.centro}</TableCell>
                                        <TableCell align="right" sx={{ color: 'primary.main' }}>{formatCurrency(row.valorReceitas)}</TableCell>
                                        <TableCell align="right">{formatCurrency(row.valorDespesas)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main' }}>{formatCurrency(row.receitasRecebidas)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'error.main' }}>{formatCurrency(row.receitasEnviadas)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main' }}>{formatCurrency(row.despesasRecebidas)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'error.main' }}>{formatCurrency(row.despesasEnviadas)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>{formatCurrency(row.saldoReceitas)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: Math.abs(row.despesasEnviadas) > 0 && Math.abs(row.saldoDespesas) > 0.01 ? 'error.main' : 'text.primary' }}>
                                            {formatCurrency(row.saldoDespesas)}
                                            {Math.abs(row.despesasEnviadas) > 0 && Math.abs(row.saldoDespesas) > 0.01 && (
                                                <Typography variant="caption" display="block" color="error">(Não Zerado)</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Tabela 2: Lançamentos por Conta Contábil (Balancete) */}
            {activeTab === 1 && (
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="h6">Lançamentos por Conta Contábil (Balancete)</Typography>
                    </Box>
                    <TableContainer sx={{ maxHeight: '60vh' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Conta Contábil</TableCell>
                                    <TableCell align="right">Valor Total (Balancete)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dataContabil.map((row, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>{row.conta}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>{formatCurrency(row.valor)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Tabela 3: Rateios Recebidos (Detalhado) */}
            {activeTab === 2 && (
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="h6">Detalhe dos Rateios Recebidos</Typography>
                    </Box>
                    <TableContainer sx={{ maxHeight: '60vh' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Destino (Quem Recebeu)</TableCell>
                                    <TableCell>Origem (Quem Pagou)</TableCell>
                                    <TableCell>Regra (Critério/Tipo)</TableCell>
                                    <TableCell align="right">Valor Alocado</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dataRecebidos.map((row, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>{row.destino}</TableCell>
                                        <TableCell>{row.origem}</TableCell>
                                        <TableCell>{row.regra}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'medium' }}>{formatCurrency(row.valor)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Tabela 4: Rastreabilidade de Rateios (Nova) */}
            {activeTab === 3 && (
                <Box>
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Filtrar por Origem"
                                    variant="outlined"
                                    value={filterOrigem}
                                    onChange={(e) => setFilterOrigem(e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><FaSearch /></InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Filtrar por Destino"
                                    variant="outlined"
                                    value={filterDestino}
                                    onChange={(e) => setFilterDestino(e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><FaSearch /></InputAdornment>,
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6">Rastreabilidade de Rateios (Origem &rarr; Destino)</Typography>
                        </Box>
                        <TableContainer sx={{ maxHeight: '60vh' }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Origem (De onde saiu)</TableCell>
                                        <TableCell>Destino (Para onde foi)</TableCell>
                                        <TableCell>Tipo</TableCell>
                                        <TableCell>Regra</TableCell>
                                        <TableCell align="right">Valor</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRastreabilidade.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell sx={{ fontWeight: 'medium' }}>{row.origem}</TableCell>
                                            <TableCell sx={{ fontWeight: 'medium' }}>{row.destino}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.tipo}
                                                    size="small"
                                                    color={row.tipo === 'Receita' ? 'success' : 'error'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{row.regra}</TableCell>
                                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>{formatCurrency(row.valor)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredRastreabilidade.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">Nenhuma transação encontrada com os filtros atuais.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box sx={{ p: 2, textAlign: 'right', bgcolor: 'grey.50' }}>
                            <Typography variant="caption" color="textSecondary">
                                Total de registros: {filteredRastreabilidade.length}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            )}
        </Box>
    );
};

export default RelatorioConferencia;
